import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  addDoc,
  writeBatch,
  serverTimestamp,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Course, Lesson, Attachment, StudentEnrollment, CourseCode } from '../types';
import { invalidateLessonCache } from './lessonService';

// كاش للدورات
interface CourseCache {
  [courseId: string]: {
    data: Course;
    timestamp: number;
    lessonsCount: number;
  };
}

interface CourseListCacheItem {
  data: Course[];
  timestamp: number;
  lastVisible?: QueryDocumentSnapshot;
}

const courseCache: CourseCache = {};
const courseListCache: Record<string, CourseListCacheItem> = {};
const COURSE_CACHE_EXPIRY = 5 * 60 * 1000; // 5 دقائق
const COURSE_LIST_CACHE_EXPIRY = 2 * 60 * 1000; // دقيقتان

// دالة للحصول على دورة مع التخزين المؤقت
export const getCourse = async (courseId: string): Promise<Course | null> => {
  try {
    const now = Date.now();
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (courseCache[courseId] && now - courseCache[courseId].timestamp < COURSE_CACHE_EXPIRY) {
      console.log(`استخدام بيانات الدورة المخزنة مؤقتًا للدورة: ${courseId}`);
      return courseCache[courseId].data;
    }
    
    const courseDoc = await getDoc(doc(db, 'courses', courseId));
    
    if (courseDoc.exists()) {
      const courseData = {
        id: courseDoc.id,
        ...courseDoc.data()
      } as Course;
      
      // تخزين البيانات في الكاش
      courseCache[courseId] = {
        data: courseData,
        timestamp: now,
        lessonsCount: courseData.lessonsCount || 0
      };
      
      return courseData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting course:', error);
    throw error;
  }
};

// دالة لإلغاء صلاحية كاش الدورة عند تحديث أو حذف دورة
export const invalidateCourseCache = (courseId: string) => {
  if (courseCache[courseId]) {
    delete courseCache[courseId];
  }
  
  // إلغاء صلاحية كاش قائمة الدورات
  Object.keys(courseListCache).forEach(key => {
    delete courseListCache[key];
  });
};

// كاش للدروس حسب الدورة
interface CourseLessonsCacheItem {
  data: Lesson[];
  timestamp: number;
}

const courseLessonsCache: Record<string, CourseLessonsCacheItem> = {};
const COURSE_LESSONS_CACHE_EXPIRY = 5 * 60 * 1000; // 5 دقائق

// كاش للمرفقات
interface AttachmentsCacheItem {
  data: Attachment[];
  timestamp: number;
}

const attachmentsCache: Record<string, AttachmentsCacheItem> = {};
const ATTACHMENTS_CACHE_EXPIRY = 5 * 60 * 1000; // 5 دقائق

// دالة لإلغاء صلاحية كاش الدروس حسب الدورة
export const invalidateCourseLessonsCache = (courseId: string) => {
  if (courseLessonsCache[courseId]) {
    delete courseLessonsCache[courseId];
  }
};

// دالة لإلغاء صلاحية كاش المرفقات
export const invalidateAttachmentsCache = (lessonId: string) => {
  if (attachmentsCache[lessonId]) {
    delete attachmentsCache[lessonId];
  }
};

// الحد الأقصى لحجم الملف: 50 ميجابايت
export const MAX_FILE_SIZE = 50 * 1024 * 1024;

// تحقق من نوع الملف
export const isAllowedFileType = (file: File): boolean => {
  // قائمة أنواع الملفات المسموح بها
  const allowedTypes = [
    // مستندات
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // صور
    'image/jpeg',
    'image/png',
    'image/gif',
    // أرشيف
    'application/zip',
    // نصوص
    'text/plain'
  ];
  
  return allowedTypes.includes(file.type);
};

// تحويل حجم الملف إلى صيغة مقروءة
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 بايت';
  
  const k = 1024;
  const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// الحصول على قائمة الدورات
export const getCourses = async (
  page = 1, 
  pageSize = 10, 
  filters: Record<string, any> = {}
): Promise<Course[]> => {
  try {
    const now = Date.now();
    const cacheKey = `courses_${page}_${pageSize}_${JSON.stringify(filters)}`;
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (courseListCache[cacheKey] && now - courseListCache[cacheKey].timestamp < COURSE_LIST_CACHE_EXPIRY) {
      console.log(`استخدام بيانات قائمة الدورات المخزنة مؤقتًا: ${cacheKey}`);
      return courseListCache[cacheKey].data;
    }
    
    const coursesRef = collection(db, 'courses');
    const constraints: any[] = [];
    
    // إضافة الفلاتر إلى الاستعلام
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    
    if (filters.level) {
      constraints.push(where('level', '==', filters.level));
    }
    
    if (filters.instructorId) {
      constraints.push(where('instructorId', '==', filters.instructorId));
    }
    
    // إضافة الترتيب والحد إلى الاستعلام
    constraints.push(orderBy('createdAt', 'desc'));
    
    // إذا كانت هناك صفحة سابقة، استخدم startAfter
    let coursesQuery;
    if (page > 1 && filters.lastVisible) {
      coursesQuery = query(
        coursesRef,
        ...constraints,
        startAfter(filters.lastVisible),
        limit(pageSize)
      );
    } else {
      coursesQuery = query(
        coursesRef,
        ...constraints,
        limit(pageSize)
      );
    }
    
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses: Course[] = [];
    let lastVisible: QueryDocumentSnapshot | undefined;
    
    if (!coursesSnapshot.empty) {
      lastVisible = coursesSnapshot.docs[coursesSnapshot.docs.length - 1];
      
      coursesSnapshot.forEach(doc => {
        const data = doc.data();
        courses.push({
          id: doc.id,
          title: data.title || '',
          teacherId: data.teacherId || '',
          teacherName: data.teacherName || '',
          price: data.price || 0,
          isPublished: data.isPublished || false,
          description: data.description || '',
          category: data.category || '',
          imageUrl: data.imageUrl || '',
          lessonsCount: data.lessonsCount || 0,
          enrolledStudents: data.enrolledStudents || [],
          createdAt: data.createdAt || Date.now(),
          updatedAt: data.updatedAt || Date.now(),
          ...data
        } as Course);
      });
    }
    
    // تخزين البيانات في الكاش
    courseListCache[cacheKey] = {
      data: courses,
      timestamp: now,
      lastVisible
    };
    
    return courses;
  } catch (error) {
    console.error('Error getting courses:', error);
    throw error;
  }
};

// الحصول على دورة بواسطة المعرف
export const getCourseById = async (id: string): Promise<Course | null> => {
  try {
    return await getCourse(id);
  } catch (error) {
    console.error('Error getting course by id:', error);
    throw error;
  }
};

// تحديث دورة
export const updateCourse = async (courseId: string, courseData: Partial<Course>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'courses', courseId), {
      ...courseData,
      updatedAt: serverTimestamp()
    });
    
    // إلغاء صلاحية الكاش
    invalidateCourseCache(courseId);
  } catch (error) {
    console.error('Error updating course:', error);
    throw error;
  }
};

// حذف دورة
export const deleteCourse = async (courseId: string): Promise<void> => {
  try {
    // الحصول على جميع الدروس المرتبطة بالدورة
    const lessonsQuery = query(
      collection(db, 'lessons'),
      where('courseId', '==', courseId)
    );
    
    const lessonsSnapshot = await getDocs(lessonsQuery);
    const batch = writeBatch(db);
    
    // حذف جميع الدروس المرتبطة بالدورة
    lessonsSnapshot.forEach((lessonDoc) => {
      batch.delete(doc(db, 'lessons', lessonDoc.id));
      
      // إلغاء صلاحية كاش الدرس
      invalidateLessonCache(lessonDoc.id);
    });
    
    // حذف الدورة
    batch.delete(doc(db, 'courses', courseId));
    
    await batch.commit();
    
    // إلغاء صلاحية الكاش
    invalidateCourseCache(courseId);
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
};

// الحصول على الدروس حسب معرف الدورة
export const getLessonsByCourseId = async (courseId: string): Promise<Lesson[]> => {
  try {
    const now = Date.now();
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (courseLessonsCache[courseId] && now - courseLessonsCache[courseId].timestamp < COURSE_LESSONS_CACHE_EXPIRY) {
      console.log(`استخدام بيانات الدروس المخزنة مؤقتًا للدورة: ${courseId}`);
      return courseLessonsCache[courseId].data;
    }
    
    const lessonsQuery = query(
      collection(db, 'lessons'),
      where('courseId', '==', courseId),
      orderBy('order', 'asc')
    );
    
    const lessonsSnapshot = await getDocs(lessonsQuery);
    const lessons: Lesson[] = [];
    
    lessonsSnapshot.forEach((doc) => {
      lessons.push({
        id: doc.id,
        ...doc.data() as Record<string, any>
      } as Lesson);
    });
    
    // تخزين البيانات في الكاش
    courseLessonsCache[courseId] = {
      data: lessons,
      timestamp: now
    };
    
    return lessons;
  } catch (error) {
    console.error('Error getting lessons by course id:', error);
    throw error;
  }
};

// الحصول على مرفقات الدرس
export const getLessonAttachments = async (lessonId: string): Promise<Attachment[]> => {
  try {
    const now = Date.now();
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (attachmentsCache[lessonId] && now - attachmentsCache[lessonId].timestamp < ATTACHMENTS_CACHE_EXPIRY) {
      console.log(`استخدام بيانات المرفقات المخزنة مؤقتًا للدرس: ${lessonId}`);
      return attachmentsCache[lessonId].data;
    }
    
    const lessonDoc = await getDoc(doc(db, 'lessons', lessonId));
    
    if (lessonDoc.exists()) {
      const lessonData = lessonDoc.data();
      const attachments = lessonData.attachments || [];
      
      // تخزين البيانات في الكاش
      attachmentsCache[lessonId] = {
        data: attachments,
        timestamp: now
      };
      
      return attachments;
    }
    
    return [];
  } catch (error) {
    console.error('Error getting lesson attachments:', error);
    throw error;
  }
};

// تحميل ملف مرفق للدرس
export const uploadLessonAttachment = async (
  file: File,
  courseId: string,
  lessonId: string,
  onProgress?: (progress: number) => void
): Promise<Attachment> => {
  try {
    // التحقق من نوع الملف
    if (!isAllowedFileType(file)) {
      throw new Error('نوع الملف غير مسموح به');
    }
    
    // التحقق من حجم الملف
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`حجم الملف يتجاوز الحد المسموح به (${formatFileSize(MAX_FILE_SIZE)})`);
    }
    
    // إنشاء اسم فريد للملف
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${file.name}`;
    
    // إنشاء مرجع للملف في Firebase Storage
    const filePath = `courses/${courseId}/lessons/${lessonId}/attachments/${fileName}`;
    const fileRef = ref(storage, filePath);
    
    // تحميل الملف مع تتبع التقدم
    const uploadTask = uploadBytesResumable(fileRef, file);
    
    // إنشاء وعد لتتبع التقدم وإكمال التحميل
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${progress}%`);
          
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          console.error('Error uploading file:', error);
          reject(error);
        },
        async () => {
          try {
            // الحصول على URL للتنزيل
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // إنشاء كائن المرفق
            const attachment: Attachment = {
              id: timestamp.toString(),
              name: file.name,
              type: file.type,
              size: file.size,
              url: downloadURL,
              path: filePath,
              createdAt: timestamp
            };
            
            // تحديث وثيقة الدرس بإضافة المرفق
            const lessonRef = doc(db, 'lessons', lessonId);
            const lessonDoc = await getDoc(lessonRef);
            
            if (lessonDoc.exists()) {
              const lessonData = lessonDoc.data();
              const attachments = lessonData.attachments || [];
              
              await updateDoc(lessonRef, {
                attachments: [...attachments, attachment],
                updatedAt: serverTimestamp()
              });
              
              // إلغاء صلاحية الكاش
              invalidateAttachmentsCache(lessonId);
              invalidateLessonCache(lessonId);
              
              resolve(attachment);
            } else {
              throw new Error('الدرس غير موجود');
            }
          } catch (error) {
            console.error('Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw error;
  }
};

// حذف مرفق من الدرس
export const deleteLessonAttachment = async (
  courseId: string,
  lessonId: string,
  attachmentName: string
): Promise<void> => {
  try {
    // الحصول على بيانات الدرس
    const lessonRef = doc(db, 'lessons', lessonId);
    const lessonDoc = await getDoc(lessonRef);
    
    if (lessonDoc.exists()) {
      const lessonData = lessonDoc.data();
      const attachments = lessonData.attachments || [];
      
      // البحث عن المرفق المطلوب حذفه
      const attachmentIndex = attachments.findIndex((a: Attachment) => a.name === attachmentName);
      
      if (attachmentIndex !== -1) {
        const attachment = attachments[attachmentIndex];
        
        // حذف الملف من Firebase Storage
        if (attachment.path) {
          const fileRef = ref(storage, attachment.path);
          await deleteObject(fileRef);
        } else {
          // إذا لم يكن هناك مسار محدد، استخدم المسار المتوقع
          const filePath = `courses/${courseId}/lessons/${lessonId}/attachments/${attachment.name}`;
          const fileRef = ref(storage, filePath);
          try {
            await deleteObject(fileRef);
          } catch (error) {
            console.warn('File not found in storage, continuing with attachment removal');
          }
        }
        
        // إزالة المرفق من قائمة المرفقات
        attachments.splice(attachmentIndex, 1);
        
        // تحديث وثيقة الدرس
        await updateDoc(lessonRef, {
          attachments,
          updatedAt: serverTimestamp()
        });
        
        // إلغاء صلاحية الكاش
        invalidateAttachmentsCache(lessonId);
        invalidateLessonCache(lessonId);
      } else {
        throw new Error('المرفق غير موجود');
      }
    } else {
      throw new Error('الدرس غير موجود');
    }
  } catch (error) {
    console.error('Error deleting attachment:', error);
    throw error;
  }
};

// دالة لتسجيل جلسة مشاهدة الفيديو
export const recordWatchSession = async (
  courseId: string,
  lessonId: string,
  userId: string,
  sessionData: {
    watchedSeconds: number;
    totalSeconds: number;
    completionPercentage: number;
    lastPosition: number;
    isCompleted: boolean;
    watchCount: number;
  }
): Promise<void> => {
  try {
    const watchSessionRef = doc(db, 'watchSessions', `${userId}_${courseId}_${lessonId}`);
    await setDoc(watchSessionRef, {
      userId,
      courseId,
      lessonId,
      ...sessionData,
      lastWatched: Date.now()
    }, { merge: true });
    
    // تحديث تقدم الفيديو أيضًا
    await updateVideoProgress(
      courseId,
      lessonId,
      userId,
      sessionData.completionPercentage,
      sessionData.totalSeconds
    );
    
    console.log(`تم تسجيل جلسة المشاهدة للمستخدم: ${userId}, الدرس: ${lessonId}`);
  } catch (error) {
    console.error('Error recording watch session:', error);
    throw error;
  }
};

// دالة للحصول على أكواد الدورة
export const getCourseCodes = async (
  teacherId: string,
  checkExpiry: boolean = false
): Promise<CourseCode[]> => {
  try {
    const codesRef = collection(db, 'courseCodes');
    const codesQuery = query(codesRef, where('createdBy', '==', teacherId));
    
    const codesSnapshot = await getDocs(codesQuery);
    
    const codes: CourseCode[] = [];
    const now = Date.now();
    
    codesSnapshot.forEach(doc => {
      const codeData = doc.data() as Record<string, any>;
      const isExpired = checkExpiry && codeData.expiryDate && codeData.expiryDate < now;
      
      // تأكد من أن حقل isUsed موجود وصحيح
      const isUsed = codeData.isUsed === true;
      
      codes.push({
        id: doc.id,
        ...codeData,
        isUsed: isUsed,
        isExpired: isExpired || false
      } as CourseCode);
    });
    
    // طباعة معلومات تصحيحية
    console.log('تم استرجاع الأكواد:', codes.length);
    console.log('الأكواد المستخدمة:', codes.filter(code => code.isUsed === true && code.usedBy).length);
    
    return codes;
  } catch (error) {
    console.error('Error getting course codes:', error);
    throw error;
  }
};

// دالة لتحديث تاريخ انتهاء صلاحية كود الدورة
export const updateCourseCodeExpiryDate = async (
  codeId: string,
  expiryDays: number
): Promise<void> => {
  try {
    // حساب تاريخ انتهاء الصلاحية بناءً على عدد الأيام
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expiryDays);
    
    await updateDoc(doc(db, 'courseCodes', codeId), {
      expiryDate: expiryDate.getTime(),
      isExpired: false,
      updatedAt: Date.now()
    });
    
    console.log(`تم تحديث تاريخ انتهاء صلاحية الكود: ${codeId}`);
  } catch (error) {
    console.error('Error updating course code expiry date:', error);
    throw error;
  }
};

// دالة لإنشاء أكواد دورة جديدة
export const generateCourseCodes = async (
  courseId: string,
  count: number,
  expiryDays: number,
  teacherId: string = ''
): Promise<{ codes: CourseCode[] }> => {
  try {
    const batch = writeBatch(db);
    const codes: CourseCode[] = [];
    
    // حساب تاريخ انتهاء الصلاحية بناءً على عدد الأيام
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expiryDays);
    
    for (let i = 0; i < count; i++) {
      // إنشاء كود فريد من 8 أحرف
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const codeRef = doc(collection(db, 'courseCodes'));
      
      const codeData: CourseCode = {
        id: codeRef.id,
        code,
        courseId,
        isUsed: false,
        createdAt: Date.now(),
        createdBy: teacherId,
        expiryDate: expiryDate.getTime(),
        isExpired: false
      };
      
      batch.set(codeRef, codeData);
      codes.push(codeData);
    }
    
    await batch.commit();
    
    return { codes };
  } catch (error) {
    console.error('Error generating course codes:', error);
    throw error;
  }
};

// دالة للحصول على إحصائيات استخدام أكواد الدورة
export const getCourseCodesUsage = async (
  courseId: string,
  startDate?: Date,
  endDate?: Date
): Promise<any[]> => {
  try {
    const codesRef = collection(db, 'courseCodes');
    let codesQuery = query(codesRef, where('courseId', '==', courseId));
    
    if (startDate) {
      codesQuery = query(codesQuery, where('createdAt', '>=', startDate.getTime()));
    }
    
    if (endDate) {
      codesQuery = query(codesQuery, where('createdAt', '<=', endDate.getTime()));
    }
    
    const codesSnapshot = await getDocs(codesQuery);
    const usageData: any[] = [];
    
    codesSnapshot.forEach(doc => {
      const codeData = doc.data();
      usageData.push({
        id: doc.id,
        ...codeData,
        usedAt: codeData.usedAt ? new Date(codeData.usedAt) : null,
        createdAt: new Date(codeData.createdAt),
        expiryDate: new Date(codeData.expiryDate)
      });
    });
    
    return usageData;
  } catch (error) {
    console.error('Error getting course codes usage:', error);
    throw error;
  }
};

// دالة للحصول على إحصائيات أكواد الدورة
export const getCourseCodesStats = async (courseId: string): Promise<any> => {
  try {
    const codesRef = collection(db, 'courseCodes');
    const codesQuery = query(codesRef, where('courseId', '==', courseId));
    const codesSnapshot = await getDocs(codesQuery);
    
    let totalCodes = 0;
    let usedCodes = 0;
    let expiredCodes = 0;
    let validCodes = 0;
    
    const now = Date.now();
    
    codesSnapshot.forEach(doc => {
      const codeData = doc.data();
      totalCodes++;
      
      if (codeData.isUsed) {
        usedCodes++;
      }
      
      if (codeData.expiryDate < now && !codeData.isUsed) {
        expiredCodes++;
      }
      
      if (codeData.expiryDate >= now && !codeData.isUsed) {
        validCodes++;
      }
    });
    
    return {
      totalCodes,
      usedCodes,
      expiredCodes,
      validCodes,
      usageRate: totalCodes > 0 ? (usedCodes / totalCodes) * 100 : 0
    };
  } catch (error) {
    console.error('Error getting course codes stats:', error);
    throw error;
  }
};

// دالة للتحقق من تسجيل الطالب في الدورة
export const checkEnrollment = async (
  courseId: string,
  userId: string
): Promise<{
  isEnrolled: boolean;
  enrollmentData?: any;
  videoProgress?: Record<string, any>;
}> => {
  try {
    const enrollmentRef = doc(db, 'enrollments', `${userId}_${courseId}`);
    const enrollmentDoc = await getDoc(enrollmentRef);
    
    if (enrollmentDoc.exists()) {
      // الحصول على بيانات التسجيل
      const enrollmentData = enrollmentDoc.data();
      
      // الحصول على تقدم الفيديو
      const progressRef = doc(db, 'progress', `${userId}_${courseId}`);
      const progressDoc = await getDoc(progressRef);
      
      return {
        isEnrolled: true,
        enrollmentData,
        videoProgress: progressDoc.exists() ? progressDoc.data() : {}
      };
    }
    
    return {
      isEnrolled: false,
      videoProgress: {}
    };
  } catch (error) {
    console.error('Error checking enrollment:', error);
    throw error;
  }
};

// دالة لتسجيل تقدم الفيديو
export const updateVideoProgress = async (
  courseId: string,
  lessonId: string,
  userId: string,
  progress: number,
  duration: number = 0
): Promise<void> => {
  try {
    const progressRef = doc(db, 'progress', `${userId}_${courseId}_${lessonId}`);
    await setDoc(progressRef, {
      userId,
      courseId,
      lessonId,
      progress,
      duration,
      updatedAt: Date.now()
    }, { merge: true });
    
    console.log(`تم تحديث تقدم الفيديو للمستخدم: ${userId}, الدرس: ${lessonId}`);
  } catch (error) {
    console.error('Error updating video progress:', error);
    throw error;
  }
};

// دالة للحصول على جميع الدورات
export const getAllCourses = async (
  page = 1,
  pageSize = 10,
  category?: string
): Promise<Course[]> => {
  try {
    console.log('بدء استرداد الدورات...');
    const coursesRef = collection(db, 'courses');
    let coursesQuery: any;
    
    if (category) {
      coursesQuery = query(
        coursesRef,
        where('category', '==', category),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    } else {
      coursesQuery = query(
        coursesRef,
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }
    
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses: Course[] = [];
    
    // استرداد بيانات المدرسين مباشرة
    for (const courseDoc of coursesSnapshot.docs) {
      const courseData = courseDoc.data() as Record<string, any>;
      const courseId = courseDoc.id;
      
      console.log(`معالجة الدورة: ${courseId}, teacherId: ${courseData.teacherId}`);
      
      let teacherName = 'غير معروف';
      let teacherImage = '';
      
      // استرداد بيانات المدرس إذا كان هناك معرف للمدرس
      if (courseData.teacherId) {
        try {
          const teacherDocRef = doc(db, 'users', courseData.teacherId);
          const teacherDoc = await getDoc(teacherDocRef);
          
          if (teacherDoc.exists()) {
            const teacherData = teacherDoc.data() as Record<string, any>;
            console.log(`تم استرداد بيانات المدرس للدورة ${courseId}:`, {
              teacherId: courseData.teacherId,
              teacherName: teacherData.name,
              teacherPhotoURL: teacherData.photoURL,
              teacherProfilePicture: teacherData.profilePicture
            });
            
            // تعيين اسم المدرس من البيانات المستردة
            if (teacherData.name) {
              teacherName = teacherData.name;
            } else if (courseData.teacherName) {
              teacherName = courseData.teacherName;
            }
            
            // تعيين صورة المدرس من البيانات المستردة
            if (teacherData.photoURL && typeof teacherData.photoURL === 'string' && teacherData.photoURL.trim() !== '') {
              teacherImage = teacherData.photoURL;
              console.log(`استخدام photoURL: ${teacherImage}`);
            } else if (teacherData.profilePicture && typeof teacherData.profilePicture === 'string' && teacherData.profilePicture.trim() !== '') {
              teacherImage = teacherData.profilePicture;
              console.log(`استخدام profilePicture: ${teacherImage}`);
            } else {
              console.log('لم يتم العثور على صورة للمدرس');
            }
          } else {
            console.log(`لم يتم العثور على بيانات المدرس للدورة ${courseId} بمعرف المدرس ${courseData.teacherId}`);
            // استخدام اسم المدرس من بيانات الدورة إذا كان متاحًا
            if (courseData.teacherName) {
              teacherName = courseData.teacherName;
            }
          }
        } catch (error) {
          console.error(`خطأ في استرداد بيانات المدرس للدورة ${courseId}:`, error);
          // استخدام اسم المدرس من بيانات الدورة في حالة حدوث خطأ
          if (courseData.teacherName) {
            teacherName = courseData.teacherName;
          }
        }
      } else if (courseData.teacherName) {
        // إذا لم يكن هناك معرف للمدرس ولكن هناك اسم للمدرس في بيانات الدورة
        teacherName = courseData.teacherName;
        console.log(`استخدام اسم المدرس من بيانات الدورة: ${teacherName}`);
      }
      
      console.log(`القيم النهائية للدورة ${courseId}:`, {
        teacherName,
        teacherImage
      });
      
      // إضافة الدورة إلى القائمة مع بيانات المدرس
      const courseObj: Course = {
        id: courseId,
        title: courseData.title || '',
        teacherId: courseData.teacherId || '',
        teacherName: teacherName,
        teacherImage: teacherImage,
        price: courseData.price || 0,
        isPublished: courseData.isPublished || false,
        description: courseData.description || '',
        category: courseData.category || '',
        imageUrl: courseData.imageUrl || '',
        lessonsCount: courseData.lessonsCount || 0,
        enrolledStudents: courseData.enrolledStudents || [],
        createdAt: courseData.createdAt || Date.now(),
        updatedAt: courseData.updatedAt || Date.now()
      };
      
      // إضافة أي خصائص إضافية من بيانات الدورة
      Object.keys(courseData).forEach(key => {
        if (!(key in courseObj) && key !== 'id') {
          (courseObj as any)[key] = courseData[key];
        }
      });
      
      courses.push(courseObj);
    }
    
    console.log(`تم استرداد ${courses.length} دورة بنجاح`);
    return courses;
  } catch (error) {
    console.error('Error getting all courses:', error);
    throw error;
  }
};

// دالة للحصول على المدرسين
export const getTeachers = async (): Promise<any[]> => {
  try {
    const usersRef = collection(db, 'users');
    const teachersQuery = query(usersRef, where('role', '==', 'teacher'));
    const teachersSnapshot = await getDocs(teachersQuery);
    
    const teachers: any[] = [];
    teachersSnapshot.forEach(doc => {
      teachers.push({
        id: doc.id,
        ...doc.data() as Record<string, any>
      });
    });
    
    return teachers;
  } catch (error) {
    console.error('Error getting teachers:', error);
    throw error;
  }
};

// دالة للحصول على الدورات المسجل فيها الطالب
export const getStudentEnrolledCourses = async (
  userId: string
): Promise<StudentEnrollment[]> => {
  try {
    const enrollmentsRef = collection(db, 'enrollments');
    const enrollmentsQuery = query(enrollmentsRef, where('userId', '==', userId));
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    
    const enrollments: StudentEnrollment[] = [];
    enrollmentsSnapshot.forEach(doc => {
      const enrollmentData = doc.data();
      enrollments.push({
        id: doc.id,
        userId: enrollmentData.userId,
        courseId: enrollmentData.courseId,
        courseName: enrollmentData.courseName || '',
        enrollmentDate: enrollmentData.enrollmentDate || Date.now(),
        isActive: enrollmentData.isActive || true,
        completionPercentage: enrollmentData.completionPercentage || 0,
        lastAccessDate: enrollmentData.lastAccessDate || Date.now(),
        paymentStatus: enrollmentData.paymentStatus || 'completed',
        codeUsed: enrollmentData.codeUsed || '',
        isPermanent: enrollmentData.isPermanent || false,
        createdAt: enrollmentData.createdAt || Date.now(),
        updatedAt: enrollmentData.updatedAt || Date.now()
      } as StudentEnrollment);
    });
    
    return enrollments;
  } catch (error) {
    console.error('Error getting student enrolled courses:', error);
    throw error;
  }
};

// دالة للحصول على إحصائيات لوحة تحكم الطالب
export const getStudentDashboardStats = async (
  userId: string
): Promise<any> => {
  try {
    // الحصول على الدورات المسجل فيها
    const courses = await getStudentEnrolledCourses(userId);
    
    // إحصائيات عامة
    const stats = {
      totalCourses: courses.length,
      completedCourses: 0,
      inProgressCourses: 0,
      totalLessons: 0,
      completedLessons: 0,
      totalProgress: 0
    };
    
    // الحصول على تقدم الطالب في كل دورة
    for (const course of courses) {
      const lessons = await getLessonsByCourseId(course.courseId);
      stats.totalLessons += lessons.length;
      
      let courseCompletedLessons = 0;
      
      for (const lesson of lessons) {
        const progressRef = doc(db, 'progress', `${userId}_${course.courseId}_${lesson.id}`);
        const progressDoc = await getDoc(progressRef);
        
        if (progressDoc.exists() && progressDoc.data().progress >= 0.9) {
          courseCompletedLessons++;
          stats.completedLessons++;
        }
      }
      
      // تحديد حالة الدورة
      if (courseCompletedLessons === lessons.length && lessons.length > 0) {
        stats.completedCourses++;
      } else if (courseCompletedLessons > 0) {
        stats.inProgressCourses++;
      }
    }
    
    // حساب نسبة التقدم الإجمالية
    if (stats.totalLessons > 0) {
      stats.totalProgress = (stats.completedLessons / stats.totalLessons) * 100;
    }
    
    return stats;
  } catch (error) {
    console.error('Error getting student dashboard stats:', error);
    throw error;
  }
};

// دالة لحذف كود دورة
export const deleteCourseCode = async (codeId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'courseCodes', codeId));
    console.log(`تم حذف كود الدورة: ${codeId}`);
  } catch (error) {
    console.error('Error deleting course code:', error);
    throw error;
  }
};

// دالة للحصول على الدورات حسب معرف المدرس
export const getCoursesByTeacherId = async (
  teacherId: string
): Promise<Course[]> => {
  try {
    const coursesRef = collection(db, 'courses');
    const coursesQuery = query(
      coursesRef,
      where('teacherId', '==', teacherId),
      orderBy('createdAt', 'desc')
    );
    
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses: Course[] = [];
    
    coursesSnapshot.forEach(doc => {
      courses.push({
        id: doc.id,
        ...doc.data() as Record<string, any>
      } as Course);
    });
    
    return courses;
  } catch (error) {
    console.error('Error getting courses by teacher ID:', error);
    throw error;
  }
};

// دالة لتحميل صورة الدورة
export const uploadCourseImage = async (
  courseId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      throw new Error('يجب أن يكون الملف صورة');
    }

    // التحقق من حجم الملف (الحد الأقصى 5 ميجابايت)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('يجب أن يكون حجم الصورة أقل من 5 ميجابايت');
    }

    // إنشاء اسم فريد للملف
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;
    
    // إنشاء مرجع للملف في Firebase Storage
    const storageRef = ref(storage, `courses/${courseId}/images/${fileName}`);
    
    // تحميل الملف مع تتبع التقدم
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // تحديث التقدم
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          // معالجة الأخطاء
          console.error('Error uploading course image:', error);
          reject(error);
        },
        async () => {
          // اكتمال التحميل بنجاح
          try {
            // الحصول على رابط التنزيل
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // تحديث الدورة بعنوان URL للصورة الجديدة
            await updateDoc(doc(db, 'courses', courseId), {
              imageUrl: downloadURL,
              updatedAt: serverTimestamp()
            });
            
            // إلغاء صلاحية الكاش
            invalidateCourseCache(courseId);
            
            resolve(downloadURL);
          } catch (error) {
            console.error('Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Error in uploadCourseImage:', error);
    throw error;
  }
};

// دالة لإضافة درس إلى دورة
export const addLessonToCourse = async (
  lessonData: Partial<Lesson> & { courseId: string },
  teacherId: string
): Promise<string> => {
  try {
    const { courseId } = lessonData;
    
    if (!courseId) {
      throw new Error('معرف الدورة مطلوب');
    }
    
    // إنشاء معرف فريد للدرس
    const lessonRef = doc(collection(db, 'lessons'));
    const lessonId = lessonRef.id;
    
    // إعداد بيانات الدرس
    const lesson: Lesson = {
      id: lessonId,
      courseId,
      title: lessonData.title || '',
      description: lessonData.description || '',
      videoUrl: lessonData.videoUrl || '',
      order: lessonData.order || 0,
      duration: lessonData.duration || 0,
      attachments: lessonData.attachments || [],
      isPublished: lessonData.isPublished || false,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    // حفظ الدرس في Firestore
    await setDoc(lessonRef, lesson);
    
    // تحديث الدورة بإضافة معرف الدرس
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      const courseData = courseDoc.data();
      const lessons = courseData.lessons || [];
      
      await updateDoc(courseRef, {
        lessons: [...lessons, lessonId],
        updatedAt: serverTimestamp()
      });
      
      // إلغاء صلاحية الكاش
      invalidateCourseCache(courseId);
      invalidateCourseLessonsCache(courseId);
      
      return lessonId;
    } else {
      throw new Error('الدورة غير موجودة');
    }
  } catch (error) {
    console.error('Error adding lesson to course:', error);
    throw error;
  }
};

// دالة لتحديث معلومات الدرس
export const updateLessonInfo = async (
  lessonId: string,
  lessonData: Partial<Lesson>
): Promise<void> => {
  try {
    const lessonRef = doc(db, 'lessons', lessonId);
    const lessonDoc = await getDoc(lessonRef);
    
    if (lessonDoc.exists()) {
      // تحديث الدرس
      await updateDoc(lessonRef, {
        ...lessonData,
        updatedAt: Date.now()
      });
      
      // إلغاء صلاحية الكاش
      const courseId = lessonDoc.data().courseId;
      if (courseId) {
        invalidateCourseCache(courseId);
        invalidateCourseLessonsCache(courseId);
      }
      
      // إلغاء صلاحية كاش الدرس
      invalidateLessonCache(lessonId);
    } else {
      throw new Error('الدرس غير موجود');
    }
  } catch (error) {
    console.error('Error updating lesson:', error);
    throw error;
  }
};

// دالة لحذف درس من دورة
export const deleteLessonFromCourse = async (
  courseId: string,
  lessonId: string
): Promise<void> => {
  try {
    // حذف المرفقات أولاً
    const lessonRef = doc(db, 'lessons', lessonId);
    const lessonDoc = await getDoc(lessonRef);
    
    if (lessonDoc.exists()) {
      const lessonData = lessonDoc.data();
      const attachments = lessonData.attachments || [];
      
      // حذف جميع المرفقات من التخزين
      const deletePromises = attachments.map(async (attachment: Attachment) => {
        if (attachment.path) {
          const fileRef = ref(storage, attachment.path);
          await deleteObject(fileRef);
        } else {
          // إذا لم يكن هناك مسار محدد، استخدم المسار المتوقع
          const filePath = `courses/${courseId}/lessons/${lessonId}/attachments/${attachment.name}`;
          const fileRef = ref(storage, filePath);
          try {
            await deleteObject(fileRef);
          } catch (error) {
            console.warn('File not found in storage, continuing with attachment removal');
          }
        }
      });
      
      await Promise.all(deletePromises);
      
      // حذف الدرس من Firestore
      await deleteDoc(lessonRef);
      
      // تحديث الدورة بإزالة معرف الدرس
      const courseRef = doc(db, 'courses', courseId);
      const courseDoc = await getDoc(courseRef);
      
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        const lessons = courseData.lessons || [];
        
        await updateDoc(courseRef, {
          lessons: lessons.filter((id: string) => id !== lessonId),
          updatedAt: serverTimestamp()
        });
      }
      
      // إلغاء صلاحية الكاش
      invalidateCourseCache(courseId);
      invalidateCourseLessonsCache(courseId);
      invalidateLessonCache(lessonId);
    } else {
      throw new Error('الدرس غير موجود');
    }
  } catch (error) {
    console.error('Error deleting lesson from course:', error);
    throw error;
  }
};

// دالة لإضافة درس جديد
export const addLesson = async (
  lessonData: Partial<Lesson> & { courseId: string },
  teacherId: string
): Promise<string> => {
  try {
    // استدعاء دالة addLessonToCourse
    return await addLessonToCourse(lessonData, teacherId);
  } catch (error) {
    console.error('Error adding lesson:', error);
    throw error;
  }
};

// دالة للحصول على تفاصيل الدورة
export const getCourseDetails = async (courseId: string): Promise<any> => {
  try {
    const courseData = await getCourse(courseId);
    
    if (!courseData) {
      throw new Error('Course not found');
    }
    
    // الحصول على الدروس
    const lessons = await getLessonsByCourseId(courseId);
    
    // الحصول على معلومات المدرس
    const instructorDoc = await getDoc(doc(db, 'users', courseData.teacherId));
    const instructorData = instructorDoc.exists() ? {
      id: instructorDoc.id,
      ...instructorDoc.data() as Record<string, any>
    } : null;
    
    return {
      ...courseData,
      lessons,
      instructor: instructorData
    };
  } catch (error) {
    console.error('Error getting course details:', error);
    throw error;
  }
};

// دالة للتحقق من كود الدورة وتطبيقه
export const validateAndApplyCourseCode = async (
  code: string,
  userId: string
): Promise<{ success: boolean; courseId?: string; message: string }> => {
  try {
    // البحث عن الكود
    const codesRef = collection(db, 'courseCodes');
    const codeQuery = query(codesRef, where('code', '==', code));
    const codeSnapshot = await getDocs(codeQuery);
    
    if (codeSnapshot.empty) {
      return { success: false, message: 'الكود غير صالح' };
    }
    
    const codeDoc = codeSnapshot.docs[0];
    const codeData = codeDoc.data();
    
    // التحقق من صلاحية الكود
    const now = Date.now();
    if (codeData.expiryDate < now) {
      return { success: false, message: 'الكود منتهي الصلاحية' };
    }
    
    // التحقق من استخدام الكود
    if (codeData.isUsed) {
      return { success: false, message: 'الكود مستخدم بالفعل' };
    }
    
    // التحقق من وجود الدورة
    const courseDoc = await getDoc(doc(db, 'courses', codeData.courseId));
    if (!courseDoc.exists()) {
      return { success: false, message: 'الدورة غير موجودة' };
    }
    
    // تسجيل الطالب في الدورة
    const enrollmentRef = doc(db, 'enrollments', `${userId}_${codeData.courseId}`);
    await setDoc(enrollmentRef, {
      userId,
      courseId: codeData.courseId,
      enrolledAt: now,
      codeId: codeDoc.id
    });
    
    // تحديث حالة الكود
    await updateDoc(doc(db, 'courseCodes', codeDoc.id), {
      isUsed: true,
      usedBy: userId,
      usedAt: now
    });
    
    return { 
      success: true, 
      courseId: codeData.courseId, 
      message: 'تم تسجيلك في الدورة بنجاح' 
    };
  } catch (error) {
    console.error('Error validating course code:', error);
    throw error;
  }
};
